<template>
  <div class="datatable" :class="{contain}">

    <div class="actions" v-if="isDesktop">
      <button class="btn btn-primary"
        v-for="(action, globalActionIndex) of globalActions"
        v-if="typeof action.condition !== 'undefined' ? action.condition() : true"
        :key="'global-action-' + globalActionIndex"
        @click="(action.method || function(){})()">
        {{ $t(action.text) }}
      </button>
      <button class="btn btn-secondary"
        v-for="(action, datatableActionIndex) of datatableActions"
        :key="'datatable-action-' + datatableActionIndex"
        @click="(action.method || function(){})()">
        {{ $t(action.text) }}
      </button>
    </div>

    <div class="tableContainer" v-if="isDesktop">
      <div class="table" :style="{
        'grid-template-columns': columns.length > 0
          ? (columns.map(c => c.type === 'image' ? '140px' : 'auto').join(' ') + ((rowActions && rowActions.length) > 0 ? ' 75px' : ''))
          : 'auto'
      }">

        <!-- Title -->
        <div class="title" v-for="(column, columnIndex) of columns" :key="'column-' + columnIndex">
          <div class="orderArrows">
            <div class="name">{{ $to(column.name) }}</div>
            <div class="arrows" @click="cycleOrder(column.id)">
              <div class="arrow" :class="{active: order.filter(o => o.field === column.id).length > 0 && order.filter(o => o.field === column.id)[0].dir === 'DESC'}">
                <svgicon src="icon/arrow-full-down.svg" :height="12" :width="8"></svgicon>
              </div>
              <div class="arrow" :class="{active: order.filter(o => o.field === column.id).length > 0 && order.filter(o => o.field === column.id)[0].dir === 'ASC'}">
                <svgicon src="icon/arrow-full-down.svg" class="rotate" :height="12" :width="8"></svgicon>
              </div>
            </div>
          </div>
          <select v-model="search[column.id]" @change="loadSearch"
                  v-if="searchesQueryConditional[column.id]">
            <option :value="undefined"/>
            <option :value="e.id" v-for="e of searchesQueryConditional[column.id]">{{$to(e.name)}}</option>
          </select>
          <input type="text" v-model="search[column.id]" @change="loadSearch"
                 v-else-if="['text', 'json', 'datetime', 'json-boolean', 'multi-line-text', 'catalogs'].includes(column.type)">
          <input type="number" v-model="search[column.id]" @change="loadSearch"
                 v-else-if="['integer', 'float', 'money'].includes(column.type)">
          <input type="text" disabled
                 v-else-if="column.type === 'image'">
          <select v-model="search[column.id]" @change="loadSearch"
                  v-else-if="column.type === 'boolean'">
            <option :value="undefined"/>
            <option value="1">{{$t("yes")}}</option>
            <option value="0">{{$t("no")}}</option>
          </select>
          <select v-model="search[column.id]" @change="loadSearch"
                  v-else-if="column.choices">
            <option :value="undefined"/>
            <option :value="choice.value || $to(choice)" v-for="choice of column.choices">{{choice.tr ? $t(choice.tr) : $to(choice)}}</option>
          </select>
        </div>
        <div class="title" v-if="rowActions && rowActions.length > 0">
          &nbsp;
        </div>

        <div v-if="rows.length === 0"></div>
        <div class="none" v-if="rows.length === 0" :style="{'grid-column-end': 'span ' + (columns.length + 1)}">{{$t("datatable.no_result")}}</div>

        <!-- Data -->
        <template v-for="(row, rowIndex) of rows">
          <div class="data"
            v-for="(data, index) of row"
            :key="'row' + rowIndex + '-data-' + index"
            :class="[{first: index === 0, pointer: !!onRowClick, even: (rowIndex + 1) % 2 === 0}, 'type-' + getColumnType(index)]"
            @click="(onRowClick || function(){})(getRowData(rowIndex))">
            <span v-if="getColumnType(index) === 'money'">{{$tc(data, getCurrency(rowIndex))}}</span>
            <template v-else-if="getColumnType(index) === 'image'">
              <template v-if="data && data.endsWith('.mp4')">
                <video :src="data" type="video/mp4" controls muted></video>
              </template>
              <template v-else>
                <div class="image" :style="{'background-image': 'url(' + (data || 'img/photo-placeholder.jpg') + ')'}"></div>
              </template>
            </template>
            <template v-else-if="getColumnType(index) === 'boolean'">
              <div class="boolIndicator" :class="{true: !!data}"></div>
              <span>{{ $t(!!data ? 'yes' : 'no') }}</span>
            </template>
            <template v-else-if="getColumnType(index) === 'boolean2'">
              <div class="boolIndicator" :class="{true: !data}"></div>
              <span>{{ $t(!!data ? 'yes' : 'no') }}</span>
            </template>
            <template v-else-if="getColumnType(index) === 'boolean3'">
              <div class="boolIndicator" :class="{true: !data}"></div>
            </template>
            <span v-else-if="getColumnType(index) === 'long-json'">{{ '{...}' }}</span>
            <template v-else-if="getColumnType(index) === 'json-boolean'">
              <div class="boolIndicator" :class="{true: !data}"></div>
              <span>{{data || $t('none')}}</span>
            </template>
            <template v-else-if="getColumnType(index) === 'datetime'">
              {{ formatDateTime(data) }}
            </template>
            <template v-else-if="getColumnType(index) === 'catalogs'">
              <Tag grey v-for="(d, di) of data" :key="'tag-'+di+index">{{$to(d) || $t(`catalog.${d}`)}}</Tag>
              <i v-if="data.length === 0">{{$t('catalog.no_catalog')}}</i>
            </template>
            <template v-else-if="getColumnType(index) === 'pills'">
              <Tag grey v-for="(d, di) of (Array.isArray(data) ? data : data.split('|'))" :key="'tag-'+di+index">{{d}}</Tag>
            </template>
            <template v-else-if="getColumnType(index) === 'multi-line-text'">
              <span v-html="data.replaceAll('|', '<br>')"></span>
            </template>
            <span v-else>{{ maps && maps[index] ? maps[index](data) : data }}</span>
          </div>
          <div class="data last" :key="'actions-' + rowIndex" v-if="rowActions && rowActions.length > 0" :class="{even: (rowIndex + 1) % 2 === 0}">
            <ActionMenu icon="/icon/actions-horizontal.png">
              <a class="item" href="javascript:void(0)" v-for="(rowAction, rowActionIndex) of rowActions" :key="'row-action-' + rowActionIndex"
                 v-if="!rowAction.condition || rowAction.condition(getRowData(rowIndex))"
                 @click="rowAction.method(getRowData(rowIndex))"
              >
                {{ $t(rowAction.text) }}
              </a>
            </ActionMenu>
          </div>
        </template>

      </div>
      <div class="spinner" :class="{show: loading}">
        <img src="../assets/img/spinner.png">
      </div>
    </div>

    <div class="mobile-search-input" v-if="isMobile">
      <input class="searchMobile" v-model="search['_global']" type="text" placeholder="Rechercher" @keyup.enter="loadSearch">
      <button class="btn btn-secondary" @click="loadSearch">
        <svgicon src="icon/search.svg" :height="14" :width="14"></svgicon>
      </button>
    </div>
    <div class="tableContainerMobile" v-if="isMobile">
      <div class="section"
        v-for="(row, rowIndex) of rows"
        :key="'row-' + rowIndex"
        :class="{pointer: !!onRowClick}"
        @click="(onRowClick || function(){})(getRowData(rowIndex))">
        <div class="title">{{ getRowData(rowIndex).name }}</div>
        <table>
          <tbody>
            <tr class="row" v-for="(data, index) of row" :key="'row-' + rowIndex + '-data-' + index"
                v-if="getColumnType(index) !== 'image' && columns[index].id !== 'name'">
              <td class="name" style="width: 40%">{{ $to(columns[index].name) }}</td>
              <td class="data">
                <div class="divData">
                  <span v-if="getColumnType(index) === 'money'">{{$tc(data, getCurrency(rowIndex))}}</span>
                  <template v-else-if="getColumnType(index) === 'boolean'">
                    <div class="boolIndicator" :class="{true: !!data}"></div>
                    <span>{{ $t(!!data ? 'yes' : 'no') }}</span>
                  </template>
                  <template v-else-if="getColumnType(index) === 'boolean2'">
                    <div class="boolIndicator" :class="{true: !data}"></div>
                    <span>{{ $t(!!data ? 'yes' : 'no') }}</span>
                  </template>
                  <span v-else-if="getColumnType(index) === 'long-json'">{{ '{...}' }}</span>
                  <template v-else-if="getColumnType(index) === 'catalogs'">
                    <Tag grey v-for="(d, di) of data" :key="'tag-'+di+index">{{$to(d) || $t(`catalog.${d}`)}}</Tag>
                    <i v-if="data.length === 0">{{$t('catalog.no_catalog')}}</i>
                  </template>
                  <template v-else-if="getColumnType(index) === 'pills'">
                    <Tag grey v-for="(d, di) of (Array.isArray(data) ? data : data.split('|'))" :key="'tag-'+di+index">{{d}}</Tag>
                  </template>
                  <template v-else-if="getColumnType(index) === 'multi-line-text'">
                    <span v-html="data.replaceAll('|', '<br>')"></span>
                  </template>
                  <span v-else>{{ maps && maps[index] ? maps[index](data) : data }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <ActionMenu class="action-menu" icon="/icon/actions.png">
          <a class="item" href="javascript:void(0)" v-for="(rowAction, rowActionIndex) of rowActions" :key="'row-action-' + rowActionIndex"
              v-if="!rowAction.columnId || (getRowData(rowIndex)[rowAction.columnId] === rowAction.requiredColumnValue)"
            @click="rowAction.method(getRowData(rowIndex))">
            {{ $t(rowAction.text) }}
          </a>
        </ActionMenu>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer" :class="{mobile: isMobile}">
      <div class="perPage" v-if="isDesktop">
          {{$t("datatable.display")}}
        <select name="" id="" class="small" v-model="perPage">
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="500">500</option>
          <option value="-1" v-if="addShowAll">{{$t('all')}}</option>
        </select>
        {{$t("datatable.elements")}}
      </div>
      <div class="shown" v-if="isDesktop">
        <div v-if="totalElements > 0">
          {{$t("datatable.displaying_elements")}} {{ (currentPage - 1) * perPage + 1 }} {{$t("datatable.to")}} {{ Math.min(currentPage * perPage, totalElements) }} {{$t("datatable.of")}} {{ totalElements }}
        </div>
      </div>
      <div class="pagination">
        <div class="page first" :class="{disabled: currentPage === 1}" @click="setPage(1)" v-if="isDesktop"><div>&lsaquo;&lsaquo;</div></div>
        <div class="page previous" :class="{disabled: currentPage === 1}" @click="setPage(currentPage - 1)"><div>&lsaquo;</div></div>
        <div class="page" v-for="n in computePages()" :key="'page-' + n" @click="setPage(n)" :class="{active: currentPage === n}">{{ n }}</div>
        <div class="page next" :class="{disabled: !canGoNext}" @click="setPage(currentPage + 1)"><div>&rsaquo;</div></div>
        <div class="page last" :class="{disabled: !canGoLast}" @click="setPage(pages === 0 ? highestPageShown : pages)" v-if="isDesktop"><div>&rsaquo;&rsaquo;</div></div>
      </div>
    </div>

    <div id="printDiv" style="display: none">
      <table>
        <tr>
          <th v-for="column of columns">{{$to(column.name)}}</th>
        </tr>
        <tr v-for="row of rows">
          <td v-for="cell of row">{{ cell === true || cell === false ? (cell ? 1 : 0) : cell }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>

  import { Core } from "@/core/Core";
  import ActionMenu from "@/components/tools/ActionMenu.vue";
  import Tag from "@/components/tools/Tag.vue";

  export default {

    name: "Datatable",

    props: {
      entity: String,
      onRowClick: Function,
      globalActions: Array,
      rowActions: Array,
      contain: Boolean,
      parameters: Object,
      compact: Boolean,
      addShowAll: Boolean,
      maps: Array
    },

    components: { ActionMenu, Tag },

    data() {
      return {
        url: Core.getDatatableUrl(),
        rawQuery: null,
        columnsInDefinition: [],
        columnsIgnored: ['id', '_currency'],
        columnsQueryConditional: [],
        searchesQueryConditional: {},
        rows: [],
        perPage: 10,
        currentPage: 1,
        highestPageShown: 1,
        highestPagePossible: null,
        totalElements: 0,
        lastUUID: "",
        search: {},
        oldSearch: {},
        loading: false,
        order: [],
        datatableActions: [
          {text: "datatable.csv_download", method: this._downloadAsCsv },
          {text: "datatable.print", method: this._print },
          //{text: "datatable.visibility", method: this._columnVisibility }
        ]
      }
    },

    async mounted() {
      await this.reload();
    },

    computed: {
      columns() {
        return this.columnsInDefinition
          .filter(c => !this.columnsIgnored.includes(c.id) && (!c.conditional || this.columnsQueryConditional.includes(c.id)))
      },
      pages() {
        return Math.ceil(this.totalElements / this.perPage);
      },
      isMobile() {
        return this.$mq === 'xs' || this.$mq === 'sm' || this.$mq === 'md';
      },
      isDesktop() {
        return this.$mq === 'lg' || this.$mq === 'xl';
      },
      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      },
      canGoNext() {
        if (this.pages === 0) {
          return this.highestPagePossible === null || this.currentPage < this.highestPagePossible;
        }
        return this.currentPage < this.pages;
      },
      canGoLast() {
        if (this.pages === 0) {
          return this.currentPage < this.highestPageShown;
        }
        return this.currentPage < this.pages;
      }
    },

    watch: {
      perPage() {
        this.setPage(this.currentPage);
      },
      parameters(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.query();
          this.$forceUpdate();
        }
      },
      "$ts.state.locale"() {
        this.query();
      },
      entity() {
        this.reload()
      }
    },

    methods: {

      async reload() {
        this.columnsInDefinition = [];
        this.rows = [];
        this.loading = true;
        this.loadURLQueryParams();
        await this.loadDefinition();
        await this.query(true);
        this.loading = false;
        this.$store.commit('currentPageActions', (this.globalActions || []).concat(this.datatableActions));
      },

      formatDateTime(datetime) {
        return window.formatDateTime(this.hourFormat, datetime);
      },

      async setPage(page) {
        if (this.pages === 0) {
          this.currentPage = Math.min(Math.max(1, page), this.highestPagePossible || Number.MAX_SAFE_INTEGER);
          if (this.currentPage > this.highestPageShown) {
            this.highestPageShown = this.currentPage;
          }
        } else {
          this.currentPage = Math.min(Math.max(1, page), this.pages);
        }
        this.setURLQueryParams();
        this.query();
      },

      async loadDefinition() {
        let definition = await this.request("GET", this.url + this.entity + "/definition");
        this.columnsInDefinition = definition.fields;
      },

      async loadSearch() {
        //delete empty search parameters
        Object.keys(this.search).forEach(k => (!this.search[k] && this.search[k] !== undefined) && delete this.search[k]);
        //if search has changed, go back to page 1
        if (JSON.stringify(this.oldSearch) !== JSON.stringify(this.search)) {
          this.currentPage = 1;
        }
        this.setURLQueryParams();
        this.query();
      },

      async query() {
        this.loading = true;
        let search = this.isMobile
          ? {_global: this.search._global}
          : Object.assign({}, this.search, {_global: undefined});

        let query = await this.request("GET", this.url + this.entity + "/query", {
          locale: this.$ts.state.locale,
          perPage: this.perPage,
          page: this.currentPage,
          parameters: this.parameters,
          searches: search,
          order: this.order
        });
        this.rawQuery = query;
        this.totalElements = query.recordsFiltered === "not_available" ? null : query.recordsFiltered;

        this.columnsQueryConditional = query.conditionalColumns || [];
        this.searchesQueryConditional = query.conditionalSearches || {};
        this.rows = query.data.map(d => this.columns.map(c => d[c.id]));
        if (this.rows.length < this.perPage) {
          this.highestPagePossible = this.currentPage;
        }
        this.loading = false;
      },

      request(method, url, data) {
        return new Promise((resolve, reject) => {
          let request = new XMLHttpRequest();
          if (data && method === "GET") {
            url += "?" + this._formatObjectParameter(null, data);
            data = null;
          }
          request.open(method, url, true);
          request.onload = () => {
            if (request.status >= 200 && request.status < 400) {
              resolve(request.responseText ? JSON.parse(request.responseText) : null);
            } else {
              reject(request);
              this.loading = false;
            }
          };
          request.onerror = () => {
            this.loading = false;
          };
          if (data) {
            request.setRequestHeader("Content-type", "application/json");
          }
          if (Core.getLoginToken()) {
            request.setRequestHeader("Authorization", "Bearer " + Core.getLoginToken());
          }
          request.send(data ? JSON.stringify(data) : null);
        });
      },

      loadURLQueryParams() {
        let query = this.$router.currentRoute.query;

        if (query.sort) {
          this.order = JSON.parse(decodeURIComponent(query.sort));
        }
        if (query.q) {
          this.search = JSON.parse(decodeURIComponent(query.q));
        }
        if (query.page) {
          this.currentPage = Number.parseInt(query.page);
        }
        if (query.perPage) {
          this.perPage = Number.parseInt(query.perPage);
        }
        if (query.highest) {
          this.highestPageShown = Number.parseInt(query.highest);
        }
      },

      setURLQueryParams() {
        let route = this.$router.currentRoute;
        let search = this.isMobile ? {_global: this.search._global} : Object.assign({}, this.search, {_global: undefined});
        let q = JSON.stringify(search);
        let sort = JSON.stringify(this.order);
        let query = route.query;
        let newQuery = {};

        if (query.branch) {
          newQuery.branch = query.branch;
        }
        if (query.company) {
          newQuery.company = query.company;
        }
        if (query.category) {
          newQuery.category = query.category;
        }
        if (query.id) {
          newQuery.id = query.id;
        }
        if (query.start) {
          newQuery.start = query.start;
        }
        if (query.end) {
          newQuery.end = query.end;
        }
        if (this.currentPage > 1) {
          newQuery.page = this.currentPage;
        }
        if (this.perPage !== 10) {
          newQuery.perPage = this.perPage;
        }
        if (sort !== "[]") {
          newQuery.sort = encodeURIComponent(sort);
        }
        if (q !== "{}") {
          newQuery.q = encodeURIComponent(q);
        }
        if (this.pages === 0 && this.highestPageShown > 1) {
          newQuery.highest = this.highestPageShown;
        }
        if (newQuery !== route.query) { //don't reload if query hasn't changed
        this.$router.replace({ name: route.name, query: newQuery })
            .catch(() => {});
        }
      },

      getCurrency(rowIndex) {
        let row = this.rawQuery.data[rowIndex];
        return row['currency'] || row['_currency'];
      },

      _downloadAsCsv() {
        this._download(
          `${this.entity}_${Date.now()}.csv`,
          'data:text/csv;charset=utf-8,\uFEFF'
          + this.columns.map(column => this.$to(column.name)).join(',') + '\r\n'
          + this.rows.map(row =>
            row.map(cell => {
              if (cell === null) {
                return cell
              } else if (cell === true || cell === false) {
                return cell ? 1 : 0 //return 1 or 0
              } else if (cell.toString) {
                return cell.toString().replace(',', ' ')
              } else {
                return cell
              }
            }).join(',')
          ).join('\r\n')
        );
      },

      _download(fileName, data) {
        let encodedData = encodeURI(data);
        let link = document.createElement('a');
        link.setAttribute('download', fileName);
        link.setAttribute('href', encodedData);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },

      _print() {
        this.$htmlToPaper('printDiv');
      },

      _columnVisibility() {
        console.log('column visibility')
      },

      _formatParameter(key, value) {
        if (value === null || value === undefined) {
          return "";
        }
        if (typeof value === "object") {
          if (typeof value.length != "undefined") {
            return this._formatArrayParameter(key, value);
          }
          return this._formatObjectParameter(key, value);
        }
        if (typeof value === 'boolean') {
          value = value ? '1' : '0'
        }
        return key + "=" + value;
      },

      _formatArrayParameter(key, values) {
        let str = "";
        for (let i = 0; i < values.length; i++) {
          let param = this._formatParameter(key + "[" + i + "]", values[i]);
          if (param) {
            str += param + "&";
          }
        }
        return str.substring(0, str.length - 1);
      },

      _formatObjectParameter(key, obj) {
        let str = "";
        for (let objKey of Object.keys(obj)) {
          let param;
          if (key) {
            param = this._formatParameter(key + "[" + objKey + "]", obj[objKey]);
          } else {
            param = this._formatParameter(objKey, obj[objKey]);
          }
          if (param) {
            str += param + "&";
          }
        }
        return str.substring(0, str.length - 1);
      },

      computePages() {

        // Start from the end instead of the beginning when maximum number of pages is unknown
        if (this.pages === 0) {
          let max = Math.min(this.currentPage + 2, this.highestPageShown);
          let min = Math.max(1, max - 4);
          max = Math.min(this.highestPageShown, min + 4);
          let arr = [];
          for (let i = min; i <= max; i++) {
            arr.push(i);
          }
          return arr;
        }

        let min = Math.max(1, this.currentPage - 2);
        let max = Math.min(this.pages, min + 4);
        min = Math.max(1, max - 4);
        let arr = [];
        for (let i = min; i <= max; i++) {
          arr.push(i);
        }
        return arr;

      },

      cycleOrder(id) {
        let current = this.order.filter(o => o.field === id)[0];
        if (current) {
          if (current.dir === "ASC") {
            current.dir = "DESC";
          } else {
            this.order = this.order.filter(o => o.field !== id);
          }
        } else {
          this.order.push({field: id, dir: "ASC"});
        }
        this.query();
      },

      getRowData(index) {
        return this.rawQuery.data[index];
      },

      getColumnType(index) {
        return this.columns[index].type;
      }

    }

  };

</script>

<style lang="scss" scoped>

  .datatable {
    display: flex;
    flex-direction: column;
    position: relative;

    &.contain {
      .tableContainer .data.type-image {
        background-size: contain;
        background-repeat: no-repeat;
      }
    }
  }

  .actions {
    button {
      margin-right: 10px;
      margin-bottom: 10px;
    }
  }

  .tableContainer {
    flex-grow: 1;
    overflow-x: auto;
    overflow-y: scroll;
    background-color: white;
    border-radius: 12px;
    padding: 25px;
    margin-top: 20px;

    .title {
      padding: 0 10px 20px 10px;
      white-space: nowrap;
      input, select {
        width: 100%;
        padding: 8px;
        border-radius: 6px;
        margin-top: 2px;
      }
      &.check {
        display: flex;
        align-items: center;
      }
      .orderArrows {
        display: flex;
        .name {
          flex-grow: 1;
          font-size: 14px;
          color: #808080;
        }
        .arrows {
          cursor: pointer;
          position: relative;
          user-select: none;
          display: flex;
          fill: #bfbfbf;
          margin-left: 5px;

          .active {
            fill: red;
          }
          .rotate {
            transform: rotate(180deg);
          }
        }
      }
    }

    .data {
      background-color: white;
      padding: 20px;
      display: flex;
      align-items: center;
      &.first {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
        img {
          border-top-left-radius: 6px;
          border-bottom-left-radius: 6px;
        }
      }
      &.last {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        img {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }
      &:not(.even) {
        background-color: #fafafa;
      }
      &.even {
        background-color: white;
      }
      &.type-image {
        min-height: 100px;
        min-width: 120px;
        padding: 10px;

        .image {
          width: 100%;
          height: 100%;
          border-radius: 6px;
          background-size: cover;
          background-position: center;
        }
      }
    }

  }

  video {
    height: 68px;
    width: auto;
    border-radius: 6px;
    margin-left:auto;
    margin-right:auto;
  }

  .tableContainerMobile {

    flex-grow: 1;
    overflow-y: scroll;

    .section {
      background-color: white;
      padding: 10px 15px;
      border-radius: 4px;
      position: relative;
      &:not(:last-of-type) {
        margin-bottom: 10px;
      }
    }

    .action-menu {
      position: absolute;
      top: 0;
      right: 0;
      width: 35px;
      height: 50px;
    }

    table {
      width: 100%;
    }

    .title {
      font-size: 18px;
      font-weight: bold;
    }

    .row {
      .name {
        color: #808080;
        padding-right: 40px;
      }
      .data {
        word-wrap: break-word;
      }
      td {
        padding: 10px 0;
      }
      &:not(:last-of-type) {
        border-bottom: 1px solid #e6e6e6;
      }
    }

  }

  .divData {
    word-break: break-word;
  }

  .boolIndicator {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: #ed2937;
    margin-right: 10px;
    border-radius: 50%;
    &.true {
      background-color: #80d445;
    }
  }

  .table {
    display: grid;
    grid-template-rows: auto;
    label {
      align-self: center;
    }
  }

  .none {
    text-align: center;
    padding: 10px 0;
  }

  .select-row {
    margin: 40px 20px;
  }

  .spinner {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    opacity: 0;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.4s ease;
    user-select: none;
    img {
      animation: spin 2s ease infinite;
      @keyframes spin {
        0% {transform: none;}
        50% {transform: rotateZ(180deg)}
        100% {transform: rotateZ(360deg)}
      }
    }
    &.show {
      opacity: 1;
      pointer-events: all;
    }
  }

  .footer {
    display: flex;
    align-items: center;
    padding-top: 25px;
    color: #808080;
    font-size: 14px;
    .perPage {
      display: flex;
      align-items: center;
      select {
        margin: 0 6px;
      }
    }
    .shown {
      flex-grow: 1;
      text-align: left;
      margin: 0 20px;
      border-left: 1px solid #dbdbdb;
      padding-left: 20px;
    }
    .pagination {
      display: flex;
      div:not(:last-of-type) {
        margin-right: 10px;
      }
      .page {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: white;
        border-radius: 6px;
        color: var(--primary-color);
        cursor: pointer;
        transition: box-shadow 0.2s ease;
        &:hover {
          box-shadow: 0 2px 4px 1px rgba(0, 0, 0, 0.1);
        }
        &.active {
          background-color: var(--primary-color);
          color: white;
        }
        &.previous, &.next, &.first, &.last {
          font-size: 32px;
          & > div {
            position: relative;
            top: -2px;
          }
        }
        &.disabled {
          opacity: 0.6;
          pointer-events: none;
        }
      }
    }
    &.mobile {
      justify-content: center;
    }
  }

  .mobile-search-input {
    display: flex;
    margin-bottom: 20px;
    fill: #888;

    .btn {
      padding: 10px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border: solid 1px #d9d9d9;
      border-left: 0;
      background-color: #d9d9d9;
    }

    .searchMobile {
      flex-grow: 1;
      border-right: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .table .tag, .tableContainerMobile .tag {
    text-transform: initial;
    white-space: nowrap;

    &:not(:last-child) {
      margin-right: 5px;
    }
  }

  .tableContainerMobile .tag {
    margin-bottom: 4px;
    width: 100%;
    text-align: center;
  }

</style>
